import { ILCHttpResponse, ISendEmailResponse } from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import {
  IActiveSession,
  IAdminLoginResendMfaRequest,
  IAdminLoginResendMfaResponse,
  IAdminLoginResponse,
  IExtendedSession,
  ILoginMfaVerifyRequest,
  ILoginRequest,
  ILoginResponse,
  IResendEmailVerificationRequest,
  IResetPasswordRequest,
  ISetExpiredPasswordRequest,
  ISetExpiredPasswordResponse,
  ISetNewPasswordRequest,
  IVerifyEmailRequest,
  IVerifyEmailResponse,
  IUpdateOwnPasswordRequest,
  IUpdateOwnPasswordResponse,
  IRemoteSupportRequestResponse,
  IRemoteSupportStatusResponse,
} from '../../interfaces/auth.interfaces'
import { RootState } from '../redux/store'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

// @lat: [[auth#Login and tokens]]
export const authApi = createApi({
  reducerPath: 'authApi',
  tagTypes: ['Sessions', 'RemoteSupport'],
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/auth`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    // Only add auth headers if not already set (e.g. change-expired-password uses mfaPendingToken)
    if (token && !headers.has('authorization')) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken && !headers.has('x-company-token')) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  endpoints: (builder) => ({
    login: builder.mutation<IAdminLoginResponse, ILoginRequest>({
      query: ({ companyId, identity, password, language, captchaToken }) => ({
        url: `/admin-login`,
        method: 'POST',
        headers: {
          'x-company-Id': companyId,
        },
        body: {
          identity,
          password,
          language,
          ...(captchaToken ? { captchaToken } : {}),
        },
      }),
    }),
    loginMfaVerify: builder.mutation<IAdminLoginResponse, ILoginMfaVerifyRequest>({
      query: ({ code, mfaPendingToken }) => ({
        url: `/admin-login-mfa-verify`,
        method: 'POST',
        body: { code, mfaPendingToken },
      }),
    }),
    loginMfaResend: builder.mutation<IAdminLoginResendMfaResponse, IAdminLoginResendMfaRequest>({
      query: ({ mfaPendingToken, language }) => ({
        url: `/admin-login-mfa-resend`,
        method: 'POST',
        body: { mfaPendingToken, language },
      }),
    }),
    setExpiredPassword: builder.mutation<ISetExpiredPasswordResponse, ISetExpiredPasswordRequest>({
      query: ({ mfaPendingToken, mfaEnabled, newPassword, language }) => ({
        url: `/change-expired-password`,
        method: 'POST',
        headers: {
          Authorization: `Bearer ${mfaPendingToken}`,
        },
        body: { newPassword, mfaEnabled, language },
      }),
    }),
    resetPassword: builder.mutation<ISendEmailResponse, IResetPasswordRequest>({
      query: ({ companyId, identity, language, captchaToken }) => ({
        url: `/reset-password`,
        method: 'POST',
        body: { identity, companyId, language, captchaToken },
      }),
      transformResponse: (rawResult: ILCHttpResponse<ISendEmailResponse>, meta) => {
        return rawResult.data as ISendEmailResponse
      },
    }),
    setNewPassword: builder.mutation<ISendEmailResponse, ISetNewPasswordRequest>({
      query: ({ password, token }) => ({
        url: `/set-new-password`,
        method: 'POST',
        body: { password, token },
      }),
      transformResponse: (rawResult: ILCHttpResponse<ISendEmailResponse>) => {
        return rawResult.data as ISendEmailResponse
      },
    }),
    verifyEmail: builder.query<IVerifyEmailResponse, IVerifyEmailRequest>({
      query: ({ token }) => ({
        url: `/verify-email`,
        params: { token },
        method: 'GET',
      }),
      transformResponse: (rawResult: ILCHttpResponse<IVerifyEmailResponse>) => {
        return rawResult.data as IVerifyEmailResponse
      },
    }),
    resendEmailVerification: builder.mutation<ISendEmailResponse, IResendEmailVerificationRequest>({
      query: ({ identity, language }) => ({
        url: `/resend-email-verification`,
        method: 'POST',
        body: {
          identity,
          ...(language ? { language } : {}),
        },
      }),
      transformResponse: (rawResult: ILCHttpResponse<ISendEmailResponse>) => {
        return rawResult.data as ISendEmailResponse
      },
    }),
    updateOwnPassword: builder.mutation<IUpdateOwnPasswordResponse, IUpdateOwnPasswordRequest>({
      query: ({ oldPassword, newPassword }) => ({
        url: `/update-own-password`,
        method: 'POST',
        body: { oldPassword, newPassword },
      }),
    }),
    logout: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: `/logout`,
        method: 'POST',
      }),
    }),
    getSessions: builder.query<IActiveSession[], void>({
      query: () => ({
        url: `/sessions`,
        method: 'GET',
      }),
      providesTags: ['Sessions'],
    }),
    postExtendSession: builder.query<IExtendedSession, void>({
      query: () => ({
        url: `/extend-session`,
        method: 'POST',
      }),
    }),
    revokeSession: builder.mutation<{ success: boolean }, { jti: string }>({
      query: ({ jti }) => ({
        url: `/sessions/${jti}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Sessions'],
    }),
    getSessionsForUser: builder.query<IActiveSession[], { identity: string }>({
      query: ({ identity }) => ({
        url: `/admin/sessions/${identity}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { identity }) => [{ type: 'Sessions', id: identity }],
    }),
    revokeSessionForUser: builder.mutation<{ success: boolean }, { identity: string; jti: string }>({
      query: ({ identity, jti }) => ({
        url: `/admin/sessions/${identity}/${jti}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { identity }) => [{ type: 'Sessions', id: identity }],
    }),
    ssoTokenExchange: builder.mutation<IAdminLoginResponse, { code: string; companyId: string }>({
      // Gateway returns a flat IAdminLoginResponse (no ILCHttpResponse wrapper), unlike most auth endpoints.
      query: ({ code, companyId }) => ({
        url: `/saml/token-exchange`,
        method: 'POST',
        body: { code, companyId },
      }),
    }),
    syncUserLocale: builder.mutation<{ success: boolean }, { language: string }>({
      query: ({ language }) => ({
        url: `/user-locale`,
        method: 'PUT',
        body: { language },
      }),
      transformResponse: (rawResult: ILCHttpResponse<{ success: boolean }>) => rawResult.data as { success: boolean },
    }),
    requestRemoteSupport: builder.mutation<IRemoteSupportRequestResponse, void>({
      query: () => ({
        url: `/remote-support/request`,
        method: 'POST',
      }),
      transformResponse: (rawResult: ILCHttpResponse<IRemoteSupportRequestResponse>) => rawResult.data as IRemoteSupportRequestResponse,
      invalidatesTags: ['RemoteSupport'],
    }),
    getRemoteSupportStatus: builder.query<IRemoteSupportStatusResponse, void>({
      query: () => ({
        url: `/remote-support/status`,
        method: 'GET',
      }),
      transformResponse: (rawResult: ILCHttpResponse<IRemoteSupportStatusResponse>) => rawResult.data as IRemoteSupportStatusResponse,
      providesTags: ['RemoteSupport'],
    }),
    disconnectRemoteSupport: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: `/remote-support/disconnect`,
        method: 'POST',
      }),
      transformResponse: (rawResult: ILCHttpResponse<{ success: boolean }>) => rawResult.data as { success: boolean },
      // Ending support also removes its row from the active sessions list.
      invalidatesTags: ['RemoteSupport', 'Sessions'],
    }),
    establishRemoteSupport: builder.mutation<ILoginResponse, { token: string }>({
      // Gateway returns a flat ILoginResponse (no ILCHttpResponse wrapper), like /saml/token-exchange,
      // so this endpoint must not transformResponse to `.data`.
      query: ({ token }) => ({
        url: `/remote-support/establish`,
        method: 'POST',
        body: { token },
      }),
    }),
    getSsoSpInfo: builder.query<
      { acsUrl: string; spEntityId: string; metadataUrl: string; ssoEnabled: boolean; ssoEnforced: boolean },
      { companyId: string }
    >({
      // Gateway sp-info returns flat JSON via res.json() (no ILCHttpResponse wrapper).
      query: ({ companyId }) => ({
        url: `/saml/sp-info/${companyId}`,
        method: 'GET',
      }),
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLoginMutation,
  useResetPasswordMutation,
  useSetNewPasswordMutation,
  useVerifyEmailQuery,
  useResendEmailVerificationMutation,
  useSetExpiredPasswordMutation,
  useLoginMfaResendMutation,
  useLoginMfaVerifyMutation,
  useUpdateOwnPasswordMutation,
  useLogoutMutation,
  useGetSessionsQuery,
  useRevokeSessionMutation,
  useGetSessionsForUserQuery,
  useRevokeSessionForUserMutation,
  useLazyPostExtendSessionQuery,
  useSsoTokenExchangeMutation,
  useSyncUserLocaleMutation,
  useGetSsoSpInfoQuery,
  useLazyGetSsoSpInfoQuery,
  useRequestRemoteSupportMutation,
  useGetRemoteSupportStatusQuery,
  useDisconnectRemoteSupportMutation,
  useEstablishRemoteSupportMutation,
} = authApi
